// shared/api/affirmations.js
// Affirmations API - Affirmation management
import { supabase } from '../supabase';

/**
 * Fetch affirmations for a journey
 * @param {string} journeyId 
 * @returns {Promise<{affirmations, error}>}
 */
export const fetchAffirmationsByJourney = async (journeyId) => {
  try {
    const { data, error } = await supabase
      .from('affirmations')
      .select('*')
      .eq('journey_id', journeyId)
      .order('order_index', { ascending: true });

    if (error) throw error;
    return { affirmations: data || [], error: null };
  } catch (error) {
    console.error('Error fetching affirmations:', error);
    return { affirmations: [], error };
  }
};

/**
 * Fetch technique-specific affirmations
 * @param {string} techniqueId 
 * @returns {Promise<{affirmations, error}>}
 */
export const fetchAffirmationsByTechnique = async (techniqueId) => {
  try {
    const { data, error } = await supabase
      .from('technique_affirmations')
      .select('*')
      .eq('technique_id', techniqueId)
      .order('order_index', { ascending: true });

    if (error) throw error;
    return { affirmations: data || [], error: null };
  } catch (error) {
    console.error('Error fetching technique affirmations:', error);
    return { affirmations: [], error };
  }
};

/**
 * Save favorite affirmation
 * @param {Object} favoriteData - {user_id, session_id, affirmation_id, affirmation_text, technique_id}
 * @returns {Promise<{favorite, error}>}
 */
export const saveFavoriteAffirmation = async (favoriteData) => {
  try {
    const { data, error } = await supabase
      .from('favorite_affirmations')
      .insert({
        ...favoriteData,
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return { favorite: data, error: null };
  } catch (error) {
    console.error('Error saving favorite affirmation:', error);
    return { favorite: null, error };
  }
};

/**
 * Remove favorite affirmation
 * @param {string} favoriteId 
 * @returns {Promise<{success, error}>}
 */
export const removeFavoriteAffirmation = async (favoriteId) => {
  try {
    const { error } = await supabase
      .from('favorite_affirmations')
      .delete()
      .eq('id', favoriteId);

    if (error) throw error;
    return { success: true, error: null };
  } catch (error) {
    console.error('Error removing favorite affirmation:', error);
    return { success: false, error };
  }
};

/**
 * Fetch user's favorite affirmations
 * @param {string} userId 
 * @returns {Promise<{favorites, error}>}
 */
export const fetchFavoriteAffirmations = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('favorite_affirmations')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return { favorites: data || [], error: null };
  } catch (error) {
    console.error('Error fetching favorite affirmations:', error);
    return { favorites: [], error };
  }
};

/**
 * Save affirmation completion record
 * @param {Object} completionData - {user_id, session_id, journey_id, technique_id, total_viewed, favorites_count}
 * @returns {Promise<{completion, error}>}
 */
export const saveAffirmationCompletion = async (completionData) => {
  try {
    const { data, error } = await supabase
      .from('affirmation_completions')
      .insert({
        ...completionData,
        completed_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return { completion: data, error: null };
  } catch (error) {
    console.error('Error saving affirmation completion:', error);
    return { completion: null, error };
  }
};

/**
 * Get affirmations for today's journey (default set)
 * @returns {Promise<{affirmations, error}>}
 */
export const getDefaultAffirmations = () => {
  const defaultAffirmations = [
    { id: 1, affirmation_text: "I am worthy of all the good things coming my way", category: "general" },
    { id: 2, affirmation_text: "I trust the journey of my life", category: "general" },
    { id: 3, affirmation_text: "I am creating the reality I desire", category: "general" },
    { id: 4, affirmation_text: "I am abundant in all areas of my life", category: "general" },
    { id: 5, affirmation_text: "I am becoming the best version of myself", category: "general" }
  ];
  
  return { affirmations: defaultAffirmations, error: null };
};
